import React, { useState } from 'react';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useCreateClassMutation } from '../../store/api/localApi';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Default first-term dates to the current Irish school year (Sep 1 → Aug 31).
 * Before September, this year's Sep already passed only for the tail, so anchor
 * on: if today is on/after Sep 1, the year starts this Sep; else last Sep.
 */
function currentSchoolYear(): { name: string; startDate: string; endDate: string } {
  const now = new Date();
  const y = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    name: `School Year ${y}/${(y + 1) % 100}`,
    startDate: `${y}-09-01`,
    endDate: `${y + 1}-08-31`,
  };
}

export interface CreateClassModalProps {
  locationId: string;
  isLocationAdmin: boolean;
  isOrgLevel: boolean;
  onClose: () => void;
}

const CreateClassModal: React.FC<CreateClassModalProps> = ({
  locationId,
  isLocationAdmin,
  isOrgLevel,
  onClose,
}) => {
  const { activeLocationName, isInLocation } = useLocationContext();
  const [createClass, { isLoading }] = useCreateClassMutation();
  const [form, setForm] = useState({
    name: '',
    room: '',
    description: '',
    startTime: '09:00',
    endTime: '15:00',
    capacity: '',
    daysOfWeek: [1, 2, 3, 4, 5] as number[],
    locationId: locationId,
  });
  // §5.1: a class is created WITH its first term. Defaulted to the school year.
  const [term, setTerm] = useState(currentSchoolYear());
  const [error, setError] = useState('');

  // Org-level users must be inside a location to create classes
  if (isOrgLevel && !isInLocation) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="create-class-modal">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={onClose}
          />
          <div className="relative z-10 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create Class</h3>
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 mb-4">
              <p className="text-sm text-yellow-800">
                Enter a location to create classes. Use the navigation to select a location first.
              </p>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const toggleDay = (day: number) => {
    setForm((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day].sort(),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name || form.daysOfWeek.length === 0 || !form.locationId) {
      setError('Name, location, and at least one day are required');
      return;
    }
    const capacity = parseInt(form.capacity, 10);
    if (!Number.isFinite(capacity) || capacity < 1) {
      setError('Capacity is required and must be at least 1.');
      return;
    }
    if (!term.name || !term.startDate || !term.endDate) {
      setError('The first term needs a name and start/end dates.');
      return;
    }
    if (term.endDate < term.startDate) {
      setError('Term end date must be on or after the start date.');
      return;
    }
    try {
      await createClass({
        locationId: form.locationId,
        name: form.name,
        room: form.room || undefined,
        description: form.description || undefined,
        startTime: form.startTime,
        endTime: form.endTime,
        daysOfWeek: form.daysOfWeek,
        capacity,
        term: { name: term.name, startDate: term.startDate, endDate: term.endDate },
      }).unwrap();
      onClose();
    } catch (err: any) {
      setError(err.data?.error || 'Failed to create class');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="create-class-modal">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        <div className="relative z-10 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl w-full max-w-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create Class</h3>
          {/* Location info - always read-only, derived from navigation context */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              Location:{' '}
              <span className="font-medium">{activeLocationName || 'Current location'}</span>
            </p>
          </div>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name *</label>
              <input
                data-testid="create-class-name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
                placeholder="e.g. Morning Session"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Room</label>
              <input
                data-testid="create-class-room"
                value={form.room}
                onChange={(e) => setForm((p) => ({ ...p, room: e.target.value }))}
                maxLength={60}
                placeholder="e.g. Blue Room"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <input
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Time *</label>
                <input
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">End Time *</label>
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Days *</label>
              <div className="flex gap-2">
                {DAY_NAMES.map((name, idx) => {
                  const selected = form.daysOfWeek.includes(idx);
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => toggleDay(idx)}
                      data-testid={`create-class-day-${idx}`}
                      aria-pressed={selected}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                        selected
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Capacity per day <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                data-testid="create-class-capacity"
                min={1}
                required
                value={form.capacity}
                onChange={(e) => setForm((p) => ({ ...p, capacity: e.target.value }))}
                placeholder="Max students per running day"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Hard ceiling per running day. A Mon–Fri class with capacity 20 can have 20 students
                each day, attending different days.
              </p>
            </div>
            {/* First term (§5.1) — a class must never be term-less. */}
            <div className="rounded-md border border-gray-200 p-3 space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase">First term</p>
              <div>
                <label className="block text-sm font-medium text-gray-700">Term name *</label>
                <input
                  data-testid="create-class-term-name"
                  value={term.name}
                  onChange={(e) => setTerm((p) => ({ ...p, name: e.target.value }))}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Starts *</label>
                  <input
                    type="date"
                    data-testid="create-class-term-start"
                    value={term.startDate}
                    onChange={(e) => setTerm((p) => ({ ...p, startDate: e.target.value }))}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ends *</label>
                  <input
                    type="date"
                    data-testid="create-class-term-end"
                    value={term.endDate}
                    onChange={(e) => setTerm((p) => ({ ...p, endDate: e.target.value }))}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="create-class-submit"
                disabled={isLoading}
                className="px-4 py-2 border border-transparent rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Creating...' : 'Create Class'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateClassModal;
